import { useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { MessageSquare } from "lucide-react";
import { useQuestionsQuery } from "@/lib/queries";
import { STATUS_LABELS, STATUS_TONES } from "@/lib/question-status";
import type { PreviousQuestion, QuestionStatus } from "@/types";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select } from "@/components/ui/select";
import { ActiveFilters, FilterBar, FilterPopover, type ActiveFilter } from "@/components/ui/filter-bar";
import {
  ROW_LINK,
  SortableHead,
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
  useSort,
} from "@/components/ui/table";
import { Page, PageFill, PageHeader, Panel, PanelBody, PanelHeader, PanelTitle } from "@/components/layout/Page";

export const Route = createFileRoute("/questions/")({
  component: QuestionsIndexPage,
});

type SortKey = keyof Pick<
  PreviousQuestion,
  "created_at" | "created_by" | "title" | "is_answered" | "rating" | "comments"
>;


const dateTimeFmt = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

function QuestionsIndexPage() {
  const navigate = useNavigate();
  // The search term goes to the server rather than being applied to a page of
  // results here: filtering client-side would silently search only whatever
  // happened to be loaded, which looks identical and is wrong.
  const [search, setSearch] = useState("");
  const [mineOnly, setMineOnly] = useState(false);
  const questionsQuery = useQuestionsQuery({ search, mine: mineOnly });
  const rows = questionsQuery.data?.questions ?? [];

  const { sort, dir, headProps } = useSort<SortKey>("created_at", "desc", {
    created_at: "desc",
    rating: "desc",
    comments: "desc",
  });
  const [createdByFilter, setCreatedByFilter] = useState("");
  // Four states, not two. The badge in the table already distinguishes a
  // failed question from one still working, and a filter that folds both into
  // "Pending" cannot answer "what broke this week" — which is the only reason
  // anybody filters this column.
  const [statusFilter, setStatusFilter] = useState<"" | QuestionStatus>("");

  const clearAll = () => {
    setCreatedByFilter("");
    setStatusFilter("");
    setSearch("");
    setMineOnly(false);
  };

  const filtered = rows
    .filter((r) => {
      // The text search is the server's; these two are column filters applied
      // to the rows it returned.
      const matchesCreatedBy =
        !createdByFilter || r.created_by.toLowerCase().includes(createdByFilter.toLowerCase());
      const matchesStatus =
        !statusFilter ||
        // "pending" and "running" are one thing to a reader — the answer has
        // not arrived — and the table shows them as one badge.
        (statusFilter === "pending"
          ? r.status === "pending" || r.status === "running"
          : r.status === statusFilter);
      return matchesCreatedBy && matchesStatus;
    })
    .sort((a, b) => {
      const { key } = sort;
      let av: string | number = a[key] as string | number;
      let bv: string | number = b[key] as string | number;
      if (key === "created_at") {
        av = new Date(a.created_at).getTime();
        bv = new Date(b.created_at).getTime();
      }
      if (key === "comments") {
        av = a.comments;
        bv = b.comments;
      } else if (key === "rating") {
        // Unrated sorts below every rated question in either direction,
        // rather than being treated as a zero-star review.
        av = a.avg_rating ?? -1;
        bv = b.avg_rating ?? -1;
      }
      if (typeof av === "string" && typeof bv === "string") return av.localeCompare(bv) * dir;
      return (Number(av) - Number(bv)) * dir;
    });

  const active: ActiveFilter[] = [
    ...(search ? [{ id: "search", label: `“${search}”`, onRemove: () => setSearch("") }] : []),
    ...(mineOnly ? [{ id: "mine", label: "Asked by me", onRemove: () => setMineOnly(false) }] : []),
    ...(createdByFilter
      ? [
          {
            id: "createdBy",
            label: `Asked by: ${createdByFilter}`,
            onRemove: () => setCreatedByFilter(""),
          },
        ]
      : []),
    ...(statusFilter
      ? [
          {
            id: "status",
            label: `Status: ${STATUS_LABELS[statusFilter]}`,
            onRemove: () => setStatusFilter(""),
          },
        ]
      : []),
  ];

  return (
    <Page>
      <PageHeader
        title="Previous questions"
        description="Everything this organisation has asked so far."
      />

      <PageFill>
        <Panel>
          <PanelHeader>
            <PanelTitle>
              {/* Two different numbers, and conflating them is how a column
                  filter turns into "where did my questions go". The first is what
                  the server returned for the search; the second is what the
                  filters below left of it. */}
              {filtered.length === rows.length
                ? `${rows.length} question${rows.length === 1 ? "" : "s"}`
                : `${filtered.length} of ${rows.length} questions`}
            </PanelTitle>
            <div className="flex items-center gap-3">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search questions"
                className="h-8 w-56 text-xs lg:w-72"
                aria-label="Search questions"
              />
              <label className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
                <Checkbox checked={mineOnly} onCheckedChange={(c) => setMineOnly(c === true)} />
                Only mine
              </label>
            </div>
          </PanelHeader>

          {/* Above the table, not inside a `<th>`. The filters used to open
              inside the header cell, which grew the header and shifted every
              column beneath it, and closed leaving nothing but a tinted 11px
              icon to say the table was still filtered. */}
          <FilterBar>
            <FilterPopover
              label="Asked by"
              summary={createdByFilter || undefined}
              onClear={() => setCreatedByFilter("")}
            >
              <Input
                autoFocus
                value={createdByFilter}
                onChange={(e) => setCreatedByFilter(e.target.value)}
                placeholder="Name or email"
                className="h-8 text-xs"
                aria-label="Filter by who asked"
              />
              <p className="mt-2 text-xs text-muted-foreground">
                Matches any part of the name on the question.
              </p>
            </FilterPopover>

            <FilterPopover
              label="Status"
              summary={statusFilter ? STATUS_LABELS[statusFilter] : undefined}
              onClear={() => setStatusFilter("")}
            >
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as "" | QuestionStatus)}
                className="h-8 w-full"
                aria-label="Filter by status"
              >
                <option value="">Any status</option>
                <option value="answered">{STATUS_LABELS.answered}</option>
                <option value="pending">{STATUS_LABELS.pending}</option>
                <option value="failed">{STATUS_LABELS.failed}</option>
              </Select>
            </FilterPopover>
          </FilterBar>

          <ActiveFilters filters={active} onClearAll={clearAll} />

          {/*
            This table was `table-layout: fixed` at a hard-coded `width: 1250`
            with a nine-entry colgroup of pixel widths, which meant it was the
            same 1250px on a phone and on a 34-inch monitor — scrolled
            sideways on one and adrift in white space on the other. Percentages
            with a single `min-w` floor do the same job at any width: the two
            prose columns take the space that is going, everything else is
            sized to its content.
          */}
          <PanelBody>
            <Table className="min-w-[64rem] text-xs">
              <TableHeader>
                <TableRow>
                  <SortableHead {...headProps("created_at", "Created")} className="w-44" />
                  <SortableHead {...headProps("created_by", "Asked by")} className="w-48" />
                  <SortableHead {...headProps("title", "Title")} className="min-w-[13rem]" />
                  <TableHead className="min-w-[20rem]">Question</TableHead>
                  <SortableHead {...headProps("is_answered", "Status")} className="w-28" />
                  <TableHead className="w-24">Visibility</TableHead>
                  {/* ERIKA's Previous Questions table carried both counts, and
                      they are how you find the questions worth revisiting — a
                      thread on a question means somebody disagreed. */}
                  <SortableHead {...headProps("comments", "Discussion")} align="right" className="w-28" />
                  <SortableHead {...headProps("rating", "Rating")} align="right" className="w-28" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {questionsQuery.isLoading && <TableEmpty colSpan={8} message="Loading…" />}
                {!questionsQuery.isLoading && filtered.length === 0 && (
                  <TableEmpty
                    colSpan={8}
                    message={
                      active.length === 0
                        ? "No questions have been asked yet."
                        : "No question matches these filters."
                    }
                    action={
                      active.length > 0 ? (
                        <Button variant="secondary" size="sm" className="text-xs" onClick={clearAll}>
                          Clear all filters
                        </Button>
                      ) : undefined
                    }
                  />
                )}
                {filtered.map((r) => (
                  <TableRow
                    key={r.id}
                    interactive
                    onClick={() => void navigate({ to: "/questions/$questionId", params: { questionId: r.id } })}
                  >
                    <TableCell className="whitespace-nowrap font-mono text-[11px] tabular-nums text-muted-foreground">
                      {dateTimeFmt(r.created_at)}
                    </TableCell>
                    <TableCell className="truncate text-muted-foreground" title={r.created_by}>
                      {r.created_by}
                    </TableCell>
                    <TableCell className="font-medium">
                      <Link
                        to="/questions/$questionId"
                        params={{ questionId: r.id }}
                        className={ROW_LINK}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {r.title}
                      </Link>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      <span className="line-clamp-2">{r.question}</span>
                    </TableCell>
                    <TableCell>
                      {/* Three states, not two: a failed question is not
                          "pending", and showing it as such means a user waits
                          for something that will never arrive. */}
                      <Badge tone={STATUS_TONES[r.status]}>
                        {STATUS_LABELS[r.status]}
                      </Badge>
                    </TableCell>
                    <TableCell>{r.is_private && <Badge tone="gray">Private</Badge>}</TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {r.comments > 0 ? (
                        <span className="inline-flex items-center gap-1 tabular-nums">
                          <MessageSquare size={12} aria-hidden />
                          {r.comments}
                        </span>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right tabular-nums text-muted-foreground">
                      {/* The shared average with its sample size, not the
                          viewer's own score: "4.5" from one person and from
                          forty are different claims. */}
                      {r.avg_rating !== null ? (
                        <>
                          {r.avg_rating.toFixed(1)}
                          <span className="ml-1 opacity-70">({r.ratings})</span>
                        </>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </PanelBody>
        </Panel>
      </PageFill>
    </Page>
  );
}
